import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useGuide } from '@/features/guides/hooks/useGuide';
import { useGuideSchedule } from '@/features/guides/hooks/useGuideSchedule';
import { guidesApi, guidesKeys } from '@/features/guides/api/guides.api';
import { bookingApi, settingsApi } from '@/features/booking/api/booking.api';
import { paymentApi } from '@/features/booking/api/payment.api';
import { bookingKeys } from '@/features/booking/api/booking.api';
import { completePaymentCheckout } from '@/features/booking/utils/verifyPaymentAfterCheckout';
import {
  isGoogleAuthWithEmail,
  isValidEmail,
  resolveCheckoutEmail,
} from '@/features/booking/utils/paymentEmail';
import { userApi } from '@/features/users/api/user.api';
import { Button, PhoneInput } from '@/shared/components/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  getAvailablePricingTypes,
  PRICING_TYPE_LABELS,
} from '@/features/booking/utils/bookingPricing';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import {
  addDays,
  daysBetweenInclusive,
  formatClockTime,
  isFullDayRangeBookable,
  isTourAvailable,
  toMinutes,
} from '@/features/booking/utils/timeSlots';
import { isDayBookableFor, TripWhenPicker, type TripWhen } from './TripWhenPicker';
import {
  fromE164India,
  isValidIndianMobile,
  toE164India,
} from '@/shared/utils/phone';
import { BookingPricingType, type Destination } from '@/types';
import dayjs from 'dayjs';

export interface BookingCheckoutContext {
  guideId: string;
  travelDate?: string;
  endDate?: string;
  city?: string;
  pricingType?: BookingPricingType;
}

interface BookingCheckoutSheetProps extends BookingCheckoutContext {
  open: boolean;
  onClose: () => void;
}

const EMPTY_WHEN: TripWhen = { travelDate: '', endDate: '', startTime: '', endTime: '' };
/** Server default; only used until the schedule's own limit has loaded. */
const DEFAULT_MAX_FULL_DAY_DAYS = 14;

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function pickDefaultPricingType(types: BookingPricingType[]) {
  if (types.includes(BookingPricingType.HOURLY)) return BookingPricingType.HOURLY;
  if (types.includes(BookingPricingType.FULL_DAY)) return BookingPricingType.FULL_DAY;
  return types[0];
}

function resolveDefaultDestination(
  destinations: Destination[],
  preferredCity?: string
): Destination | null {
  if (destinations.length === 0) return null;
  if (preferredCity) {
    const match = destinations.find(
      (d) => d.name.toLowerCase() === preferredCity.toLowerCase()
    );
    if (match) return match;
  }
  return destinations[0];
}

function unitsLabel(count: number, unit: 'hour' | 'day') {
  return count === 1 ? `1 ${unit}` : `${count} ${unit}s`;
}

function SectionLabel({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'primary.main' }}>
      {icon}
      <Typography variant="subtitle2" fontWeight={800} color="text.primary">
        {children}
      </Typography>
    </Box>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value}
      </Typography>
    </Box>
  );
}

export function BookingCheckoutSheet({
  open,
  onClose,
  guideId,
  travelDate: initialDate,
  endDate: initialEndDate,
  city,
  pricingType: initialPricingType,
}: BookingCheckoutSheetProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  // Stays true through the close animation, so the content doesn't flash "Guide not found".
  const [active, setActive] = useState(open);
  useEffect(() => {
    if (open) setActive(true);
  }, [open]);
  const { data: guide, isLoading: guideLoading } = useGuide(active ? guideId : undefined);
  const [when, setWhen] = useState<TripWhen>(EMPTY_WHEN);
  const [pricingType, setPricingType] = useState<BookingPricingType | ''>('');
  const [visibleMonths, setVisibleMonths] = useState<string[]>([currentMonth()]);
  const [displayName, setDisplayName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDestinationId, setSelectedDestinationId] = useState('');
  const googleEmailLocked = isGoogleAuthWithEmail(user);
  const { travelDate, endDate, startTime, endTime } = when;
  const [maxFullDayDays, setMaxFullDayDays] = useState(DEFAULT_MAX_FULL_DAY_DAYS);
  // Also load the month a full-day trip could run into, so the days stepper isn't cut short.
  const scheduleMonths = useMemo(
    () => [
      ...visibleMonths,
      travelDate.slice(0, 7),
      endDate.slice(0, 7),
      travelDate ? addDays(travelDate, maxFullDayDays - 1).slice(0, 7) : '',
    ],
    [visibleMonths, travelDate, endDate, maxFullDayDays]
  );
  const { meta: scheduleMeta, days: scheduleDays } = useGuideSchedule(
    active ? guideId : undefined,
    scheduleMonths
  );

  useEffect(() => {
    if (scheduleMeta) setMaxFullDayDays(scheduleMeta.maxFullDayDays);
  }, [scheduleMeta]);

  const { data: bookingDestinations = [], isLoading: destinationsLoading } = useQuery({
    queryKey: guidesKeys.bookingDestinations(guideId),
    queryFn: () => guidesApi.getBookingDestinations(guideId),
    enabled: active && Boolean(guideId),
  });

  const { data: cancellationPolicy } = useQuery({
    queryKey: ['settings', 'cancellation-policy'],
    queryFn: settingsApi.getCancellationPolicy,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const preferredCity = city ?? guide?.city;
  const preferredCityMatch = useMemo(() => {
    if (!preferredCity || bookingDestinations.length === 0) return null;
    return (
      bookingDestinations.find((d) => d.name.toLowerCase() === preferredCity.toLowerCase()) ??
      null
    );
  }, [bookingDestinations, preferredCity]);

  const defaultDestination = useMemo(
    () => resolveDefaultDestination(bookingDestinations, preferredCity),
    [bookingDestinations, preferredCity]
  );

  const destination = useMemo(
    () => bookingDestinations.find((d) => d._id === selectedDestinationId) ?? null,
    [bookingDestinations, selectedDestinationId]
  );

  const cityMismatch =
    Boolean(preferredCity) && bookingDestinations.length > 0 && preferredCityMatch == null;

  const availablePricingTypes = getAvailablePricingTypes(guide?.pricing);

  useEffect(() => {
    if (!open) return;
    // The picker shrinks a seeded range that isn't free once the schedule loads.
    setWhen({
      ...EMPTY_WHEN,
      travelDate: initialDate ?? '',
      endDate: initialEndDate ?? initialDate ?? '',
    });
    setVisibleMonths([currentMonth()]);
    // Every open starts fresh; the effect below then falls back to Few hours.
    setPricingType(initialPricingType ?? '');
    setTermsAccepted(false);
    setError(null);
    setSelectedDestinationId(defaultDestination?._id ?? '');
    setDisplayName(
      user?.displayName ?? [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    );
    setContactPhone(fromE164India(user?.contactPhone ?? user?.phone));
    setContactEmail(resolveCheckoutEmail(user));
  }, [open, initialDate, initialEndDate, initialPricingType, user, defaultDestination?._id]);

  useEffect(() => {
    if (!open || !defaultDestination) return;
    setSelectedDestinationId((current) => current || defaultDestination._id);
  }, [open, defaultDestination]);

  useEffect(() => {
    if (!open || availablePricingTypes.length === 0) return;
    setPricingType((current) => current || pickDefaultPricingType(availablePricingTypes));
  }, [open, availablePricingTypes]);

  const isHourly = pricingType === BookingPricingType.HOURLY;
  const hours = startTime && endTime ? (toMinutes(endTime) - toMinutes(startTime)) / 60 : 0;
  const numberOfDays = travelDate && endDate ? daysBetweenInclusive(travelDate, endDate) : 0;
  const duration = isHourly ? hours : numberOfDays;

  // Re-checked against the live schedule so a slot taken meanwhile disables Pay.
  const isWhenValid = useMemo(() => {
    if (!scheduleMeta || !travelDate) return false;
    if (isHourly) {
      if (!startTime || !endTime) return false;
      return isTourAvailable(
        scheduleDays.get(travelDate),
        scheduleMeta,
        toMinutes(startTime),
        toMinutes(endTime)
      );
    }
    return (
      Boolean(endDate) && isFullDayRangeBookable(scheduleDays, scheduleMeta, travelDate, endDate)
    );
  }, [scheduleMeta, scheduleDays, isHourly, travelDate, endDate, startTime, endTime]);

  const quoteQuery = useQuery({
    queryKey: ['booking-quote', guideId, pricingType, duration],
    queryFn: () =>
      guidesApi.getBookingQuote(guideId, {
        pricingType: pricingType as BookingPricingType,
        duration,
        travelerCount: 1,
      }),
    enabled: Boolean(open && guideId && pricingType && duration > 0 && isWhenValid),
  });
  const quote = isWhenValid ? quoteQuery.data : undefined;

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!pricingType || !isWhenValid) throw new Error('Please pick when you need the guide');
      if (!destination) throw new Error('Please select a destination');
      const email = contactEmail.trim();
      if (!isValidEmail(email)) throw new Error('Please enter a valid email address for payment');

      const phoneE164 = toE164India(contactPhone);
      const updatedUser = await userApi.updateContact({
        displayName: displayName.trim(),
        contactPhone: phoneE164,
        email: googleEmailLocked ? undefined : email,
      });
      updateUser(updatedUser);

      const booking = await bookingApi.create({
        guideProfileId: guideId,
        destinationId: destination._id,
        pricingType,
        travelerCount: 1,
        travelDate,
        ...(isHourly ? { startTime, endTime } : { endDate }),
        travelerContact: { name: displayName.trim(), phone: phoneE164 },
      });
      const returnUrl = `${window.location.origin}/payment/return`;
      return paymentApi.initiate(booking.id, returnUrl);
    },
    onSuccess: async (payment) => {
      try {
        const verified = await completePaymentCheckout(payment);
        if (verified) {
          await queryClient.invalidateQueries({ queryKey: bookingKeys.all });
          onClose();
          navigate(`/bookings/${verified.bookingId}/confirmation`, { replace: true });
        }
      } catch (err) {
        setError((err as Error).message ?? 'Failed to confirm payment');
      }
    },
    onError: (err: Error) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        // Someone else just took this time: reload the schedule so the pickers reflect it.
        void queryClient.invalidateQueries({ queryKey: [...guidesKeys.all, 'schedule', guideId] });
        setWhen((current) => ({ ...current, startTime: '', endTime: '' }));
      }
      const serverMessage = isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(serverMessage ?? err.message ?? 'Failed to start checkout');
    },
  });

  // First thing still missing, shown above Pay so a disabled button is never a mystery.
  const payBlocker = (() => {
    if (!pricingType) return 'Choose Few hours or Full day';
    if (!travelDate) return isHourly ? 'Choose a date' : 'Choose a start date';
    if (!isWhenValid) {
      if (!scheduleMeta) return 'Loading availability…';
      if (isHourly && (!startTime || !endTime)) return 'Choose a start time';
      return 'Pick a date and time when the guide is free';
    }
    if (!destination) return 'Select a destination';
    if (displayName.trim().length < 2) return 'Enter your full name';
    if (!isValidIndianMobile(contactPhone)) return 'Enter a valid 10-digit mobile number';
    if (!isValidEmail(contactEmail)) return 'Enter a valid email';
    if (!termsAccepted) return 'Accept the cancellation policy to continue';
    if (!quote) return 'Getting the price…';
    return null;
  })();
  const canPay = payBlocker == null;

  const loading = guideLoading || destinationsLoading;
  const ready = !loading && guide != null && availablePricingTypes.length > 0;
  const currency = quote?.currency ?? guide?.pricing?.currency ?? 'INR';
  const unitPrice = isHourly ? guide?.pricing?.hourlyPrice : guide?.pricing?.fullDayPrice;
  const workingLabel = scheduleMeta
    ? `${formatClockTime(scheduleMeta.workingHours.start)} – ${formatClockTime(scheduleMeta.workingHours.end)}`
    : '';

  let whenLabel = '';
  if (isWhenValid && isHourly) {
    whenLabel = `${dayjs(travelDate).format('ddd, D MMM')} · ${formatClockTime(startTime)} – ${formatClockTime(endTime)}`;
  } else if (isWhenValid) {
    whenLabel =
      numberOfDays === 1
        ? `${dayjs(travelDate).format('ddd, D MMM')}${workingLabel ? ` · ${workingLabel}` : ''}`
        : `${dayjs(travelDate).format('D MMM')} → ${dayjs(endDate).format('D MMM')}${workingLabel ? ` · ${workingLabel} daily` : ''}`;
  }

  const handlePricingTypeChange = (type: BookingPricingType) => {
    setPricingType(type);
    // Keep the date only if the new option can start on it, so Pay never sits disabled silently.
    setWhen((current) => {
      const keep =
        current.travelDate && isDayBookableFor(type, scheduleDays.get(current.travelDate));
      const date = keep ? current.travelDate : '';
      return { travelDate: date, endDate: date, startTime: '', endTime: '' };
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionProps={{ onExited: () => setActive(false) }}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          bgcolor: 'background.paper',
          ...(!isMobile && { m: 3, maxHeight: 'calc(100dvh - 96px)' }),
        },
      }}
    >
      {/* ─── Header ─── */}
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 2.5 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {guide && (
          <Avatar
            src={guide.profilePhoto}
            sx={{ width: 48, height: 48, bgcolor: 'primary.main', flexShrink: 0 }}
          >
            {guide.fullName?.[0]?.toUpperCase() ?? 'G'}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>
            {loading ? 'Loading…' : `Book ${guide?.fullName ?? 'guide'}`}
          </Typography>
          {destination?.name && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
              {destination.name}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ flexShrink: 0, bgcolor: 'action.hover' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* ─── Content ─── */}
      {/* `&&` beats MUI's "DialogTitle + DialogContent { padding-top: 0 }" rule. */}
      <DialogContent sx={{ '&&': { px: { xs: 2, sm: 4 }, py: { xs: 2.5, sm: 3 } } }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {!loading && !guide && <Alert severity="error">Guide not found</Alert>}

        {!loading && guide && availablePricingTypes.length === 0 && (
          <Alert severity="warning">This guide has not configured pricing yet.</Alert>
        )}

        {ready && (
          <>
            {cityMismatch && destination && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {preferredCity} is not listed for this guide. This booking will use{' '}
                {destination.name}.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={{ xs: 3, md: 4 }}>
              <Grid size={{ xs: 12, md: 7 }}>
                {/* ── When ── */}
                <SectionLabel icon={<EventOutlinedIcon fontSize="small" />}>
                  When do you need the guide?
                </SectionLabel>
                <TripWhenPicker
                  pricingTypes={availablePricingTypes}
                  pricingType={pricingType}
                  onPricingTypeChange={handlePricingTypeChange}
                  value={when}
                  onChange={setWhen}
                  meta={scheduleMeta}
                  pricing={guide.pricing}
                  days={scheduleDays}
                  onMonthVisible={(month) =>
                    setVisibleMonths((months) =>
                      months.includes(month) ? months : [...months, month]
                    )
                  }
                />

                <Divider sx={{ my: 4 }} />

                {/* ── Contact ── */}
                <SectionLabel icon={<PersonOutlineIcon fontSize="small" />}>
                  Your details
                </SectionLabel>
                <Grid container spacing={2.5}>
                  <Grid size={12}>
                    <TextField
                      label="Full name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="As on your ID"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <PhoneInput
                      label="Phone"
                      value={contactPhone}
                      onChange={setContactPhone}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      fullWidth
                      size="small"
                      required
                      InputProps={{ readOnly: googleEmailLocked }}
                      helperText={
                        googleEmailLocked
                          ? 'From your Google account'
                          : 'Required for payment receipts'
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* ── Booking summary ── */}
              {/* Heading sits above the card, like "When…" on the left, so both line up. */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ position: { md: 'sticky' }, top: { md: 0 } }}>
                  <SectionLabel icon={<ReceiptLongOutlinedIcon fontSize="small" />}>
                    Booking summary
                  </SectionLabel>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: 2,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                    }}
                  >
                    <SummaryRow
                      label="Trip"
                      value={pricingType ? PRICING_TYPE_LABELS[pricingType] : '—'}
                    />
                    <SummaryRow label="When" value={whenLabel || 'Not chosen yet'} />
                    {destination && <SummaryRow label="Destination" value={destination.name} />}

                    <Divider sx={{ my: 1.5 }} />

                    {quote && unitPrice != null ? (
                      <>
                        <SummaryRow
                          label={`${formatGuidePrice(unitPrice, currency)} × ${unitsLabel(
                            duration,
                            isHourly ? 'hour' : 'day'
                          )}`}
                          value={formatGuidePrice(quote.grossAmount, currency)}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            mt: 1,
                          }}
                        >
                          <Typography fontWeight={800}>Total</Typography>
                          <Typography variant="h5" fontWeight={900} color="primary.main">
                            {formatGuidePrice(quote.grossAmount, currency)}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {quoteQuery.isFetching
                          ? 'Getting the price…'
                          : 'The price appears once you pick when.'}
                      </Typography>
                    )}

                    {cancellationPolicy && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
                          {cancellationPolicy.summary}
                        </Typography>
                      </Alert>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          size="small"
                          color="primary"
                          sx={{ p: 0.75 }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                          I agree to the booking terms and cancellation policy
                        </Typography>
                      }
                      sx={{ mt: 2, mx: 0, alignItems: 'center', gap: 0.5 }}
                    />
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      {/* ─── Footer ─── */}
      {ready && (
        <DialogActions
          sx={{
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            px: { xs: 2, sm: 4 },
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            '& > :not(style) ~ :not(style)': { ml: 0 },
          }}
        >
          {payBlocker && !checkoutMutation.isPending && (
            <Typography variant="body2" color="text.secondary" textAlign="center" role="status">
              {payBlocker}
            </Typography>
          )}
          <Button
            intent="primary-dark"
            fullWidth
            size="large"
            disabled={!canPay || checkoutMutation.isPending}
            onClick={() => checkoutMutation.mutate()}
            sx={{ py: 1.5, borderRadius: 2, fontWeight: 800, fontSize: '1rem', textTransform: 'none' }}
          >
            {checkoutMutation.isPending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={18} color="inherit" />
                Redirecting to payment…
              </Box>
            ) : quote ? (
              `Pay ${formatGuidePrice(quote.grossAmount, currency)}`
            ) : (
              'Pay now'
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
